import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'lib/api';
import { isAxiosError } from 'axios';
import queryKeys from './queryKeys';
import queryString from 'query-string';
import type { SynchronousQueryConfig } from 'lib/react-query';

export interface GetCampaignsParams {
  facebookAccountId: string[];
  campaignNameFilter?: string;
  campaign_ids?: string[];
  status?: RowStatus;
}

export type RowStatus = 'ACTIVE' | 'PAUSED';

export interface CampaignEntry {
  id: string;
  name: string;
  status: RowStatus;
  budget: number;
  is_cbo: boolean;
}

interface ErrorResponse {
  message: string;
}

export const getCampaigns = async (
  {
    params,
  }: {
    params: GetCampaignsParams;
  },
  signal: AbortSignal,
): Promise<CampaignEntry[]> => {
  const { facebookAccountId, campaignNameFilter, status, campaign_ids } = params;

  const promises = facebookAccountId.map((id) =>
    apiClient.get<CampaignEntry[]>(
      `/api/v1/facebook_manager/get_campaigns/${id}/`,
      {
        params: {
          campaign_name_filter: campaignNameFilter,
          ...(campaign_ids?.length ? { campaign_ids } : {}),
          ...(status ? { status } : {}),
        },
        signal,
        paramsSerializer: (prms) =>
          queryString.stringify(prms, { arrayFormat: 'comma' }),
      },
    ),
  );

  try {
    const responses = await Promise.all(promises);

    const isError = responses.some((r) => isAxiosError(r));
    if (isError) {
      const errorResponses: ErrorResponse[] = responses.filter(
        (response, index) => {
          const error = isAxiosError(response);
          if (error) {
            response.message = `An error occurred while fetching campaigns for account ${facebookAccountId[index]}`;
          }
          return error;
        },
      ) as unknown as ErrorResponse[];

      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject(errorResponses);
    }

    // Filter out successful responses and handle errors
    const successfulResponses = responses.filter(
      (response) => response?.status >= 200 && response?.status <= 400,
    );
    // Return the combined data from successful responses
    return successfulResponses.flatMap((response) => response.data);
  } catch (error) {
    // Handle any other unexpected errors
    console.error(error);
    throw error;
  }
};

export const dedupeCampaignsById = (
  items: readonly CampaignEntry[],
): CampaignEntry[] => {
  const byId = new Map<string, CampaignEntry>();
  for (const item of items) {
    if (item?.id) {
      byId.set(item.id, item);
    }
  }
  return [...byId.values()];
};

export const fetchCampaignsWithUrlIds = async (
  {
    params,
  }: { params: GetCampaignsParams },
  signal: AbortSignal,
): Promise<CampaignEntry[]> => {
  const { campaign_ids, ...baseParams } = params;
  const uniqueUrlCampaignIds = [...new Set(campaign_ids ?? [])].filter(Boolean);

  if (!uniqueUrlCampaignIds.length) {
    return getCampaigns({ params }, signal);
  }

  const [matchedByIds, baseList] = await Promise.all([
    getCampaigns({ params: { ...params, campaign_ids: uniqueUrlCampaignIds } }, signal),
    getCampaigns({ params: baseParams }, signal),
  ]);

  return dedupeCampaignsById([...baseList, ...matchedByIds]);
};

interface UseCampaignsOptions {
  params: GetCampaignsParams;
  config?: SynchronousQueryConfig<typeof getCampaigns, CampaignEntry[]>;
}

// `campaign_ids` is deliberately left out of the key: the browse list is the
// identity of this query, and the ids only add campaigns the filters can't
// reach. Keying on them would refetch the whole list every time the Campaigns
// step mirrors a selection change back to the URL.
export const useCampaigns = ({ params, config }: UseCampaignsOptions) => {
  return useQuery({
    staleTime: 30_000,
    ...config,
    queryFn: ({ signal }) => fetchCampaignsWithUrlIds({ params }, signal),
    queryKey: [
      ...queryKeys.campaigns,
      params.facebookAccountId,
      params.campaignNameFilter,
      params.status,
    ],
  });
};
